import {inject, Injectable, signal} from '@angular/core';
import {JwtHelperService} from '@auth0/angular-jwt';
import {AuthenticateResponse} from '../model/auth/authenticate-response';
import {PlayerSessionService} from './player-session.service';
import {BehaviorSubject} from 'rxjs';
import {PLAYER_PROFILE_SETTING_HIDE_MENU, PlayerProfileSetting} from '../model/player-profile-setting';
import {environment} from '../../../../environments/environment';
import {ActivatedRoute, Router} from '@angular/router';
import {PLAYER_PREFIX_FULL} from '../player.routes';

@Injectable(
  {
    providedIn: 'root'
  }
)
export class PlayerAuthenticationService
{
  public playerSessionService = inject(PlayerSessionService);
  private jwtHelper: JwtHelperService;
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);

  private loggedInSource = new BehaviorSubject<boolean>(false);
  loggedInStatus = this.loggedInSource.asObservable();

  private loggedInSignal$ = signal<boolean>(false);
  loggedInSignal = this.loggedInSignal$.asReadonly();

  constructor()
  {
    this.jwtHelper = new JwtHelperService();
    const authenticated = this.isAuthenticated();
    this.loggedInSource.next(authenticated);
    this.loggedInSignal$.set(authenticated);
  }

  public routeToLogin(withReturnUrl: boolean): void
  {
    const returnUrl = this.router.url;
    const url = environment.frontendPrefix + '/player';
    if (withReturnUrl && !returnUrl?.startsWith('/backend'))
    {
      this.router.navigate([url],
        {
          queryParams: {returnUrl}
        }).then(() => {});
    }
    else
    {
      this.router.navigate([url]).then(() => {});
    }
  }

  public loginSuccessfulRoute(): void
  {
    const returnUrl = this.activatedRoute.snapshot.queryParamMap.get('returnUrl');
    const url = returnUrl ? returnUrl : PLAYER_PREFIX_FULL + '/profile';
    this.router.navigate([url]).then(() => {});
  }

  public loginSuccess(data: AuthenticateResponse): void
  {
    console.log('loginSuccess, Spielernummer: '  + data.playerProfile?.playerNumber + ', data: ', data);
    this.playerSessionService.playerProfileObject = data.playerProfile;
    this.token = data.token;
    this.refreshToken = data.refreshToken;
    this.removeForgotPasswordData();
    this.initUserSettings(data.userSettings);
  }

  private initUserSettings(settings: PlayerProfileSetting[]): void
  {
    if (settings?.length > 0)
    {
      for (const setting of settings)
      {
        if (setting.settingCd === PLAYER_PROFILE_SETTING_HIDE_MENU)
          this.playerSessionService.setSettingMenuHidden(setting.value === '1', false);
      }
    }
  }

  public logoutSuccess(): void
  {
    console.log('logoutSuccess()');

    // localStorage.removeItem('2KPL-USER'); -> Benutzername behalten
    localStorage.removeItem('2KPL-TOKEN');
    localStorage.removeItem('2KPL-REFFRESH-TOKEN');
    localStorage.removeItem('2KPL-EXPTIME');
    localStorage.removeItem('2KPL-PAIDTO');
    this.playerSessionService.removeSessionVariables();
    this.removeForgotPasswordData();

    setTimeout(() =>
    {
      this.loggedInSource.next(false);
      this.loggedInSignal$.set(false);
    });
  }

  public removeForgotPasswordData(): void
  {
    sessionStorage.removeItem('2KPL-ENTER_REGISTRATION_CODE');
    sessionStorage.removeItem('2KPL-REGISTRATION_USERNAME');
  }

  private initToken(token: string): void
  {
    const decodedToken = this.jwtHelper.decodeToken(token);
    console.log('Token: decodedToken', decodedToken);

    if (decodedToken)
    {
      this.username = decodedToken.sub;
      this.expirationTime = decodedToken.exp;
      this.paidTo = decodedToken.paidTo;

      const roles: string[] = decodedToken.roles;
      console.log('Token: roles', roles);

      if (roles.includes('ROLE_ADMIN'))
      {
        this.admin = true;
        console.log('Token: Spieler hat Rolle "ADMIN"');
      }

      setTimeout(() =>
      {
        this.loggedInSource.next(true);
        this.loggedInSignal$.set(true);
      });
    }
  }


  public isAuthenticated(): boolean
  {
    const token = this.token;
    if (token?.length > 0)
    {
      const tokenExpired = this.jwtHelper.isTokenExpired(token);
      if (tokenExpired)
      {
        const refreshToken = this.refreshToken;
        if (refreshToken)
        {
          const refreshTokenExpired = this.jwtHelper.isTokenExpired(refreshToken);
          console.log('isAuthenticated, refreshTokenExpired: ' + refreshTokenExpired + ', Token: ' + refreshToken);
          return !refreshTokenExpired;
        }
        console.log('[PLAYER] isAuthenticated, tokenExpired: ' + tokenExpired + ', Token: ' + token);
      }
      return !tokenExpired;
    }
    return false;
  }
  get token(): string
  {
    return this.getStringValueLS('2KPL-TOKEN', '');
  }
  set token(value: string)
  {
    if (value)
    {
      localStorage.setItem('2KPL-TOKEN', value);
      console.log('Token in LocalStorage gespeichert: ' + value);
      this.initToken(value);
    }
    else localStorage.removeItem('2KPL-TOKEN');
  }
  get refreshToken(): string
  {
    return this.getStringValueLS('2KPL-REFFRESH-TOKEN', '');
  }
  set refreshToken(value: string)
  {
    localStorage.setItem('2KPL-REFFRESH-TOKEN', value);
    console.log('Refresh-Token in LocalStorage gespeichert: ' + value);
  }
  get username(): string
  {
    return this.getStringValueLS('2KPL-USER', '');
  }
  set username(value: string)
  {
    localStorage.setItem('2KPL-USER', value);
  }
  public isProVersion(): boolean
  {
    const paidTo = this.paidTo;
    console.log('isProVersion, paidTo: ' + paidTo);
    if (paidTo)
      return new Date().getTime() < paidTo.getTime();
    else return false;
  }
  get paidTo(): Date
  {
    const value = this.getStringValueLS('2KPL-PAIDTO', undefined);
    if (value)
      return new Date(value);
    else return undefined;
  }
  set paidTo(value: string)
  {
    localStorage.setItem('2KPL-PAIDTO', value);
  }
  get expirationTime(): number
  {
    return this.getNumberValueLS('2KPL-EXPTIME', 0);
  }
  set expirationTime(value: number)
  {
    localStorage.setItem('2KPL-EXPTIME', value + '');
  }
  get admin(): boolean
  {
    return this.getBooleanValueLS('2KPL-IS_ADMIN', false);
  }
  set admin(value: boolean)
  {
    if (value)
      localStorage.setItem('2KPL-IS_ADMIN', '1');
    else localStorage.removeItem('2KPL-IS_ADMIN');
  }

  get enterRegistrationCode(): boolean
  {
    return this.getBooleanValueSS('2KPL-ENTER_REGISTRATION_CODE', false);
  }
  set enterRegistrationCode(value: boolean)
  {
    sessionStorage.setItem('2KPL-ENTER_REGISTRATION_CODE', value ? '1' : '0');
  }
  get registrationUsername(): string
  {
    return this.getStringValueSS('2KPL-REGISTRATION_USERNAME', '');
  }
  set registrationUsername(value: string)
  {
    sessionStorage.setItem('2KPL-REGISTRATION_USERNAME', value);
  }

  public getStringValueLS(key: string, defaultValue: string): string
  {
    const value = localStorage.getItem(key);
    if (value === undefined || value === null)
      return defaultValue;

    return value;
  }
  private getBooleanValueLS(key: string, defaultValue: boolean): boolean
  {
    const value = localStorage.getItem(key);
    if (value === undefined || value === null)
      return defaultValue;

    return value === '1' || value === 'true';
  }
  private getNumberValueLS(key: string, defaultValue: number): number {
    const value = localStorage.getItem(key);
    if (value === undefined || value === null)
      return defaultValue;

    return +value;
  }

  private getBooleanValueSS(key: string, defaultValue: boolean): boolean
  {
    const value = sessionStorage.getItem(key);
    if (value === undefined || value === null)
      return defaultValue;

    return value === '1' || value === 'true';
  }
  public getStringValueSS(key: string, defaultValue: string): string
  {
    const value = sessionStorage.getItem(key);
    if (value === undefined || value === null)
      return defaultValue;

    return value;
  }
}
